/**
 * edgetam-client.js — «the object inside this box, on this one frame» for a page
 * that is not /remove-object-from-video.
 *
 * The segmenter itself is EdgeTamCore (edgetam-core.js); the worker that owns
 * its loading is ro-worker.js — the ORT build from window.__ortBuild, the
 * provider ladder with the known-answer test (OrtEpVerify), the fp16/fp32 model
 * pairs. A second copy of that worker would drift from the first, so this client
 * drives the same one with three of its messages: segInit, frames, select.
 *
 * First caller: /remove-text-from-video on a TRIPOD shot. With a camera that
 * never moves, nothing tells a caption's plate from the wall behind it by
 * motion — the layer engine fell back to boxing the letters and left the white
 * pill on screen (bench «agnes», 02.10.2026, leftover 0.66). Prompted with the
 * letters' box grown by ¾ of its height, EdgeTAM answered the pill at IoU 0.98
 * and a translucent CapCut box at 0.98; outlined text without a plate came back
 * with an object score near zero, which is the honest answer «no plate here».
 *
 *   var seg = EdgeTamClient.create({ workerUrl, engineUrls, model, feat });
 *   var r = await seg.boxMask(rgba, w, h, {x, y, w, h});   // null when unavailable
 *   // r: { mask:Uint8Array(w*h)|null, bbox, area, iou, obj, reason, provider }
 *   seg.dispose();
 *
 * A provider that answered wrong is remembered for 30 days under the SAME key
 * /remove-object uses (model + adapter + browser) — one device, one verdict.
 */
(function (root) {
    'use strict';
    if (root.EdgeTamClient) return;

    function create(cfg) {
        cfg = cfg || {};
        var worker = null, seq = 0, pend = new Map(), readyP = null, vKey = null, adapter = null;
        var feat = typeof cfg.feat === 'function' ? cfg.feat : function () {};

        function verdictKeyFor(ad) {
            var V = root.OrtEpVerify;
            var model = (cfg.model && cfg.model.webgpu && cfg.model.webgpu.enc) || 'edgetam';
            var parts = ['edgetam-1', model.split('/').pop().split('?')[0], (ad && (ad.vendor + '/' + ad.architecture)) || 'gpu?', navigator.userAgent.slice(0, 40)];
            return V && V.verdictKey ? V.verdictKey(parts) : parts.join('|');
        }
        function onVerdict(v) {
            var V = root.OrtEpVerify;
            try {
                if (V && vKey && v && !v.ok && !v.error && !v.skipped && v.provider !== 'wasm') V.remember(vKey, v.provider, { kind: 'edgetam-kat', iou: v.iou });
            } catch (_) { }
        }
        function onMessage(ev) {
            var m = ev.data || {};
            if (m.type === 'segVerdict') { onVerdict(m.verdict); return; }
            var p = m.id != null ? pend.get(m.id) : null;
            if (!p) return;
            pend.delete(m.id);
            p.resolve(m);
        }
        function call(msg, transfer) {
            return new Promise(function (resolve, reject) {
                if (!worker) { reject(new Error('edgetam-client: no worker')); return; }
                msg.id = ++seq;
                pend.set(msg.id, { resolve: resolve, reject: reject });
                worker.postMessage(msg, transfer || []);
            });
        }

        /* → {ok, provider, ...} — init + segInit once; a failure stays a failure */
        function ready() {
            if (readyP) return readyP;
            readyP = (async function () {
                var ob = root.__ortBuild;
                if (!cfg.workerUrl || !cfg.model) return { ok: false, reason: 'no-config' };
                if (!ob || typeof ob.url !== 'function') return { ok: false, reason: 'no-ort-owner' };
                var t0 = performance.now();
                worker = new Worker(cfg.workerUrl);
                worker.onmessage = onMessage;
                worker.onerror = function (e) {
                    var err = new Error('edgetam-client: worker died ' + ((e && e.message) || ''));
                    pend.forEach(function (p) { p.reject(err); });
                    pend.clear();
                };
                await call({ type: 'init', urls: cfg.engineUrls || [] });
                var eps = (typeof ob.wasmOnly === 'function' && ob.wasmOnly()) ? ['wasm'] : ['webgpu', 'wasm'];
                var V = root.OrtEpVerify;
                adapter = (V && V.adapterIdentity && eps[0] === 'webgpu') ? await V.adapterIdentity() : null;
                vKey = verdictKeyFor(adapter);
                var rejected = {};
                if (V && V.isRejected) eps.forEach(function (ep) { if (ep !== 'wasm' && V.isRejected(vKey, ep)) rejected[ep] = 1; });
                var r = await call({
                    type: 'segInit', ortUrl: ob.url(), ortFallback: typeof ob.fallback === 'function' ? ob.fallback() : null,
                    wasmBase: typeof ob.wasmBase === 'function' ? ob.wasmBase() : null, model: cfg.model, eps: eps, rejected: rejected
                });
                feat('edgetam_ready', { ok: r.ok ? 1 : 0, provider: r.provider || '', ms: Math.round(performance.now() - t0),
                    mb: r.bytes ? +(r.bytes / 1048576).toFixed(1) : 0, reason: r.reason ? String(r.reason).slice(0, 80) : '', rungs: r.rungs || 0, retries: r.retries || 0 });
                return r;
            })().catch(function (e) { return { ok: false, reason: String((e && e.message) || e).slice(0, 120) }; });
            return readyP;
        }

        /**
         * boxMask(rgba, w, h, box) → {mask, bbox, area, iou, obj, reason, provider, ms} | null
         * null — the segmenter is unavailable on this device; mask null — it ran and
         * found nothing it would call an object in that box.
         */
        async function boxMask(rgba, w, h, box) {
            var rd = await ready();
            if (!rd || !rd.ok) return null;
            var buf = new Uint8ClampedArray(rgba).buffer;
            await call({ type: 'frames', frames: [{ buf: buf, t: 0 }], w: w, h: h, cropLane: false }, [buf]);
            var s = await call({ type: 'select', index: 0, points: [], box: { x: box.x, y: box.y, w: box.w, h: box.h } });
            var out = { mask: null, bbox: s.bbox || null, area: s.area || 0, iou: s.iou || 0, obj: s.obj, reason: s.reason || '', provider: s.provider || rd.provider, ms: s.ms || 0 };
            if (s.mask) out.mask = new Uint8Array(s.mask);
            return out;
        }

        /**
         * setFrames(list, w, h) — hand the worker the pictures ONCE; taps that
         * follow re-use the stored frames and the cached embedding of the frame
         * they land on. list: [Uint8ClampedArray|Uint8Array rgba of w×h]; all
         * frames share one size (a smaller picture is letterboxed by the caller).
         * → false when the segmenter is unavailable on this device.
         */
        async function setFrames(list, w, h) {
            var rd = await ready();
            if (!rd || !rd.ok) return false;
            var frames = [], transfer = [];
            list.forEach(function (rgba, i) { var b = new Uint8ClampedArray(rgba).buffer; frames.push({ buf: b, t: i }); transfer.push(b); });
            await call({ type: 'frames', frames: frames, w: w, h: h, cropLane: false }, transfer);
            return true;
        }

        /**
         * pick({index, points, box, segOpts}) → {mask, bbox, area, iou, obj, reason, provider, ms, cands}
         * Points are [{x, y, label}] (1 = this, 0 = not this) on the stored frame
         * `index`. segOpts go to EdgeTamCore.segment (mode, refine, allCands,
         * maxFrac). With allCands each cand carries its mask (Uint8Array w×h).
         */
        async function pick(o) {
            var rd = await ready();
            if (!rd || !rd.ok) return null;
            var s = await call({ type: 'select', index: o.index | 0, points: o.points || [], box: o.box || null, segOpts: o.segOpts || null });
            var out = { mask: s.mask ? new Uint8Array(s.mask) : null, bbox: s.bbox || null, area: s.area || 0, iou: s.iou || 0, obj: s.obj,
                reason: s.reason || '', provider: s.provider || rd.provider, ms: s.ms || 0, cands: null };
            if (s.cands) out.cands = s.cands.map(function (c) { return { area: c.area, bbox: c.bbox, iou: c.iou, edge: c.edge, mask: c.mask ? new Uint8Array(c.mask) : null }; });
            return out;
        }

        /* ── EdgeTAM VIDEO memory (EdgeTamVideo in the worker; WebGPU only) ──
           A clip streams one frame at a time: videoFrame() makes it the
           current frame, then a prompt and/or a step for every object on it.
           Masks come back as packed bits at the frame size (EdgeTamVideo.maskBits).
           cfg.video = {dec, menc, matt, constants, pos2} — urls. */
        var videoP = null;
        function videoReady() {
            if (videoP) return videoP;
            videoP = (async function () {
                var rd = await ready();
                if (!rd || !rd.ok) return { ok: false, reason: rd ? rd.reason || 'seg-failed' : 'seg-failed' };
                if (!cfg.video) return { ok: false, reason: 'no-config' };
                var t0 = performance.now();
                var r = await call({ type: 'vtInit', files: cfg.video });
                feat('edgetam_video_ready', { ok: r.ok ? 1 : 0, provider: r.provider || rd.provider || '', ms: Math.round(performance.now() - t0),
                    mb: r.bytes ? +(r.bytes / 1048576).toFixed(1) : 0, reason: r.reason ? String(r.reason).slice(0, 80) : '' });
                return r;
            })().catch(function (e) { return { ok: false, reason: String((e && e.message) || e).slice(0, 120) }; });
            return videoP;
        }
        async function videoFrame(rgba, w, h, idx) {
            var buf = new Uint8ClampedArray(rgba).buffer;
            return call({ type: 'vtFrame', buf: buf, w: w, h: h, idx: idx }, [buf]);
        }
        function bitsOf(r) { return r && r.bits ? { bits: new Uint8Array(r.bits), area: r.area, osl: r.osl } : null; }
        /* o: {obj, n, idx, mask?: Uint8Array(w·h) | points? | box?} on the current frame */
        async function videoPrompt(o) {
            var msg = { type: 'vtPrompt', obj: o.obj, n: o.n | 0, idx: o.idx, points: o.points || null, box: o.box || null }, tr = [];
            if (o.mask) { msg.mask = new Uint8Array(o.mask).buffer; tr.push(msg.mask); }
            var r = await call(msg, tr);
            if (r.error) throw new Error('edgetam-video: ' + r.error);
            return bitsOf(r);
        }
        /* → {objId: {bits, area, osl} | null (a prompted frame)} */
        async function videoStep(objs, idx, dir) {
            var r = await call({ type: 'vtStep', objs: objs, idx: idx, dir: dir });
            if (r.error) throw new Error('edgetam-video: ' + r.error);
            var out = {};
            Object.keys(r.res || {}).forEach(function (k) { out[k] = bitsOf(r.res[k]); });
            return out;
        }
        function videoDrop(obj) { return worker ? call({ type: 'vtDrop', obj: obj }) : Promise.resolve(); }

        function dispose() {
            try { if (worker) worker.terminate(); } catch (_) { }
            worker = null; readyP = null; videoP = null;
            pend.forEach(function (p) { p.reject(new Error('edgetam-client: disposed')); });
            pend.clear();
        }

        return { ready: ready, boxMask: boxMask, setFrames: setFrames, pick: pick, dispose: dispose,
                 videoReady: videoReady, videoFrame: videoFrame, videoPrompt: videoPrompt, videoStep: videoStep, videoDrop: videoDrop };
    }

    root.EdgeTamClient = { create: create };
})(typeof window !== 'undefined' ? window : self);
